import { ReactElement } from 'react'
import styled, { css } from 'styled-components'

import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import { Label } from 'components/v2/Label'
import { CheckIcon } from 'components/Icons/CheckIcon'
import { ICheckboxGroupProps, ICheckboxProps } from './types'
import { safeFormatMessage } from 'utils/intl'
import { useIntl } from 'react-intl'

const Input = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  opacity: 0;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const Content = styled.div<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'pointer')};
`

const disableCheckboxStyle = css<{ isChecked?: boolean; disable?: boolean }>`
  background-color: ${({ isChecked }): string => (isChecked ? '#E3E3E3' : '#ffffff')};
  border: 1px solid ${({ isChecked }): string => (isChecked ? '#E3E3E3' : '#F0F0F0')};
  cursor: not-allowed;
`

const CheckboxIconContainer = styled.div<{ isChecked?: boolean; disabled?: boolean }>`
  align-items: center;
  background-color: ${({ isChecked }): string => (isChecked ? '#916EE8' : 'transparent')};
  border: 1px solid ${({ isChecked }): string => (isChecked ? '#916EE8' : '#B0B0B0')};
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  height: 16px;
  padding: 0px;
  transition: 0.3s;
  width: 16px;
  min-width: 16px;
  min-height: 16px;
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'pointer')};
  ${({ disabled }): typeof disableCheckboxStyle | undefined => (disabled ? disableCheckboxStyle : undefined)}

  &:hover {
    border: 1px solid ${({ isChecked }): string => (isChecked ? '#916EE8' : '#28265d')};
  }
`

const StyledCheckIcon = styled(CheckIcon)`
  fill: #ffffff;
  height: 12px;
  width: 12px;
  min-width: 12px;
  min-height: 12px;
`

const CheckboxContainer = styled.label`
  display: flex;
`

const Checkbox = ({ className, children, ...inputProps }: ICheckboxProps): ReactElement => {
  const disabled = inputProps.disabled || inputProps.readOnly
  return (
    <CheckboxContainer className={className}>
      <Input type="checkbox" {...inputProps} />
      <Content disabled={disabled}>
        <CheckboxIconContainer isChecked={inputProps.checked} disabled={disabled}>
          {inputProps.checked && <StyledCheckIcon />}
        </CheckboxIconContainer>
        {children && <Heading.Body>{children}</Heading.Body>}
      </Content>
    </CheckboxContainer>
  )
}

const CheckboxGroupContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
`

const CheckboxGroupFlex = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 16px;
`

export const CheckboxGroup = ({
  className,
  label,
  messages,
  items,
  values,
  onChange,
  touched,
  errors
}: ICheckboxGroupProps): ReactElement => {
  const intl = useIntl()
  const showError = Boolean(touched && errors)

  const handleChange = (newValue: string): void => {
    if (values.includes(newValue)) {
      onChange(values.filter((value) => value !== newValue))
    } else {
      onChange([...values, newValue])
    }
  }

  return (
    <CheckboxGroupContainer className={className}>
      {label ? <Label>{label}</Label> : null}
      <CheckboxGroupFlex>
        {items.map((item) => (
          <Checkbox id={item} key={item} checked={values.includes(item)} onChange={(): void => handleChange(item)}>
            {safeFormatMessage({ intl, key: item, messages })}
          </Checkbox>
        ))}
      </CheckboxGroupFlex>
      {showError && <ErrorMessage>{errors}</ErrorMessage>}
    </CheckboxGroupContainer>
  )
}

export const CheckboxWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: space-between;

  input {
    margin-left: 0;
  }
`
export { Checkbox }
